import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);
  const hotArticles = articles.filter((a) => a.isHot).slice(0, 5);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 黑料天天动漫 | 动漫圈${category.name}爆料`;
    } else {
      document.title = '分类未找到 - 黑料天天动漫';
    }
  }, [category]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="container-custom text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <p className="text-foreground-500">该分类不存在或已被移除</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const loadMore = () => {
    setVisibleCount((prev) => prev + 6);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Category Header */}
        <section className="pt-20 pb-8 bg-background-100">
          <div className="container-custom">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-4 flex items-center gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-accent-500 text-background-50">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-8 md:py-12">
          <div className="container-custom">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    共 <span className="text-primary-600">{categoryArticles.length}</span> 篇文章
                  </h2>
                </div>

                {categoryArticles.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {categoryArticles.slice(0, visibleCount).map((article) => (
                        <ArticleCard key={article.id} {...article} />
                      ))}
                    </div>
                    {visibleCount < categoryArticles.length && (
                      <div className="text-center mt-8">
                        <button
                          onClick={loadMore}
                          className="px-8 py-2.5 text-sm font-medium rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加载更多 <i className="ri-arrow-down-s-line ml-1"></i>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                      <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-fire-line text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900">热门爆料</h3>
                  </div>
                  <div className="space-y-1">
                    {hotArticles.map((article) => (
                      <ArticleCard key={article.id} {...article} variant="compact" />
                    ))}
                  </div>
                </div>

                {/* Categories List */}
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">全部分类</h3>
                  <div className="space-y-1">
                    {categories.map((cat) => {
                      const count = articles.filter((a) => a.category === cat.slug).length;
                      return (
                        <a
                          key={cat.slug}
                          href={`/category/${cat.slug}`}
                          className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-colors ${cat.slug === slug ? 'bg-primary-50 text-primary-600 font-medium' : 'text-foreground-600 hover:bg-background-100'}`}
                        >
                          <span className="flex items-center gap-2">
                            <i className={`${cat.icon} text-sm`}></i>
                            {cat.name}
                          </span>
                          <span className="text-xs text-foreground-400">{count}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </div>
  );
}